import React, { useState } from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    ReferenceInput,
    AutocompleteInput,
    DateField,
    required,
    minValue,
    maxValue,
    maxLength,
    useTranslate,
    useRedirect,
    useNotify,
    useRecordContext,
    Toolbar,
    SaveButton,
    FormDataConsumer,
    useDataProvider,
    useGetList,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Alert,
    Chip,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField as MuiTextField,
    Tab,
    Tabs,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    ArrowBack,
    Bolt,
    Settings,
    Power,
    LocationOn,
    ElectricBolt,
    Warning,
    Assessment,
    EvStation,
    Delete,
} from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const KNOWN_LOAD_GROUP_FIELDS = new Set([
    'name', 'siteId', 'description', 'location',
    'maxPower', 'voltage', 'maxCurrent', 'phase',
    'priorityLevel', 'loadBalancingEnabled', 'status',
    'lmsTemplateId', 'ocppServerId',
]);

const mapLoadGroupBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const loadGroupFieldLabelKey = (field: string): string =>
    KNOWN_LOAD_GROUP_FIELDS.has(field)
        ? `resources.load-groups.fields.${field}`
        : field;

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: { xs: 2, sm: 3 } }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

const DeleteLoadGroupSection = () => {
    const record = useRecordContext();
    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();
    const translate = useTranslate();

    const hasChargers = record?.chargerStats?.totalChargers > 0;

    const handleClick = () => {
        setOpen(true);
        setConfirmText('');
    };

    const handleClose = () => {
        setOpen(false);
        setConfirmText('');
    };

    const handleConfirm = async () => {
        if (confirmText === record?.name) {
            try {
                await dataProvider.delete('load-groups', { id: record.id });
                notify(translate('resources.load-groups.notifications.deleted'), { type: 'success' });
                redirect('/load-groups');
            } catch (error) {
                notify(translate('resources.load-groups.notifications.deleteError'), { type: 'error' });
            }
            setOpen(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 2, sm: 0 } }}>
            <Box>
                <Typography variant="body2" color="text.secondary">
                    {hasChargers
                        ? translate('resources.load-groups.delete.hasChargersContent')
                        : translate('resources.load-groups.delete.content')}
                </Typography>
            </Box>
            <Button
                variant="outlined"
                onClick={handleClick}
                disabled={hasChargers}
                sx={{
                    borderColor: hasChargers ? '#BDBDBD' : '#F44336',
                    color: hasChargers ? '#BDBDBD' : '#F44336',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    '&:hover': {
                        borderColor: hasChargers ? '#BDBDBD' : '#D32F2F',
                        backgroundColor: hasChargers ? 'transparent' : 'rgba(244,67,54,0.08)',
                    },
                    '&.Mui-disabled': {
                        borderColor: '#BDBDBD',
                        color: '#BDBDBD',
                    },
                }}
                title={hasChargers
                    ? translate('resources.load-groups.delete.hasChargersContent')
                    : translate('common.delete')}
            >
                {translate('common.delete')}
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning sx={{ color: '#F44336' }} />
                        <Typography variant="h6">
                            {translate('resources.load-groups.delete.title')}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{translate('resources.load-groups.delete.warning')}</strong>
                        </Typography>
                        <Typography variant="body2">
                            {translate('resources.load-groups.delete.message')} <strong>{record?.name}</strong>.
                        </Typography>
                    </Alert>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {translate('resources.load-groups.delete.prompt')} <strong>{record?.name}</strong> {translate('resources.load-groups.delete.promptBelow')}
                    </Typography>

                    <MuiTextField
                        fullWidth
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder={translate('resources.load-groups.delete.typePlaceholder', { name: record?.name })}
                        variant="outlined"
                        autoFocus
                        error={confirmText !== '' && confirmText !== record?.name}
                        helperText={
                            confirmText !== '' && confirmText !== record?.name
                                ? translate('resources.load-groups.delete.nameDoesNotMatch')
                                : ''
                        }
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={handleClose}
                        sx={{ textTransform: 'none' }}
                    >
                        {translate('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        disabled={confirmText !== record?.name}
                        sx={{
                            backgroundColor: '#F44336',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#D32F2F',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#FFCDD2',
                            },
                        }}
                    >
                        {translate('resources.load-groups.delete.button')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const EditToolbar = () => {
    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const record = useRecordContext();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/load-groups')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.load-groups.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.load-groups.edit.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                    {record?.name} • {record?.siteName} • {translate('resources.load-groups.priority')}: {record?.priorityLevel}
                </Typography>
            </Box>
        </Box>
    );
};

const ChargersListSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { data, isLoading } = useGetList('chargers', {
        filter: { loadGroupId: record?.id },
        sort: { field: 'name', order: 'ASC' },
        pagination: { page: 1, perPage: 100 },
    });

    if (!record) return null;

    if (isMobile) {
        return (
            <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {translate('resources.load-groups.sections.chargers')} ({data?.length || 0})
                </Typography>
                {isLoading ? <LinearProgress /> : data && data.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {data.map((charger: any) => (
                            <Box key={charger.id} sx={{ p: 1.5, border: '1px solid #E0E0E0', borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{charger.name}</Typography>
                                    <Chip label={translate(`resources.chargers.statuses.${charger.status}`)} size="small" sx={{ backgroundColor: charger.status === 'available' ? '#4CAF50' : charger.status === 'charging' ? '#2196F3' : charger.status === 'error' ? '#F44336' : charger.status === 'maintenance' ? '#FF9800' : '#757575', color: 'white' }} />
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>{charger.model}</Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Typography variant="caption" color="text.secondary">{charger.maxPower} kW max</Typography>
                                    <Typography variant="caption" color="text.secondary">{charger.currentPower || 0} kW now</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Alert severity="info">{translate('resources.load-groups.noChargers')}</Alert>
                )}
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: { xs: 2, sm: 3 }, fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {translate('resources.load-groups.sections.chargers')} ({data?.length || 0})
            </Typography>

            {isLoading ? (
                <LinearProgress />
            ) : data && data.length > 0 ? (
                <Box sx={{
                    border: '1px solid rgba(224, 224, 224, 1)',
                    borderRadius: 1,
                    overflow: 'hidden',
                }}>
                    <Box sx={{
                        display: 'table',
                        width: '100%',
                        borderCollapse: 'collapse',
                    }}>
                        <Box sx={{
                            display: 'table-header-group',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        }}>
                            <Box sx={{ display: 'table-row' }}>
                                <Box sx={{ display: 'table-cell', p: 2, fontWeight: 600 }}>
                                    {translate('resources.chargers.fields.name')}
                                </Box>
                                <Box sx={{ display: 'table-cell', p: 2, fontWeight: 600 }}>
                                    {translate('resources.chargers.fields.model')}
                                </Box>
                                <Box sx={{ display: 'table-cell', p: 2, fontWeight: 600 }}>
                                    {translate('resources.chargers.fields.serialNumber')}
                                </Box>
                                <Box sx={{ display: 'table-cell', p: 2, fontWeight: 600 }}>
                                    {translate('resources.chargers.fields.power')}
                                </Box>
                                <Box sx={{ display: 'table-cell', p: 2, fontWeight: 600 }}>
                                    {translate('resources.chargers.fields.status')}
                                </Box>
                                <Box sx={{ display: 'table-cell', p: 2, fontWeight: 600 }}>
                                    {translate('resources.chargers.fields.currentPower')}
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'table-row-group' }}>
                            {data.map((charger: any) => (
                                <Box
                                    key={charger.id}
                                    sx={{
                                        display: 'table-row',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                        '&:not(:last-child)': {
                                            borderBottom: '1px solid rgba(224, 224, 224, 1)',
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'table-cell', p: 2 }}>
                                        {charger.name}
                                    </Box>
                                    <Box sx={{ display: 'table-cell', p: 2 }}>
                                        {charger.model}
                                    </Box>
                                    <Box sx={{ display: 'table-cell', p: 2 }}>
                                        {charger.serialNumber || '-'}
                                    </Box>
                                    <Box sx={{ display: 'table-cell', p: 2 }}>
                                        {charger.maxPower} kW
                                    </Box>
                                    <Box sx={{ display: 'table-cell', p: 2 }}>
                                        <span style={{ pointerEvents: 'none' }}>
                                            <Chip
                                                label={translate(`resources.chargers.statuses.${charger.status}`)}
                                                size="small"
                                                component="div"
                                                clickable={false}
                                                sx={{
                                                    backgroundColor: (() => {
                                                        switch(charger.status) {
                                                            case 'available': return '#4CAF50';
                                                            case 'charging': return '#2196F3';
                                                            case 'error': return '#F44336';
                                                            case 'maintenance': return '#FF9800';
                                                            case 'offline': return '#757575';
                                                            default: return '#E0E0E0';
                                                        }
                                                    })(),
                                                    color: 'white',
                                                }}
                                            />
                                        </span>
                                    </Box>
                                    <Box sx={{ display: 'table-cell', p: 2 }}>
                                        {charger.currentPower || 0} kW
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            ) : (
                <Alert severity="info">
                    {translate('resources.load-groups.noChargers')}
                </Alert>
            )}
        </Box>
    );
};

export const LoadGroupEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [tabValue, setTabValue] = useState(0);
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const transform = (data: any) => {
        const cleanedData = { ...data };

        if (cleanedData.voltage !== undefined) {
            cleanedData.voltage = Number(cleanedData.voltage);
        }
        if (cleanedData.maxCurrent !== undefined) {
            cleanedData.maxCurrent = Number(cleanedData.maxCurrent);
        }
        if (cleanedData.maxPower !== undefined) {
            cleanedData.maxPower = Number(cleanedData.maxPower);
        }
        if (cleanedData.priorityLevel !== undefined) {
            cleanedData.priorityLevel = Number(cleanedData.priorityLevel);
        }

        if (typeof cleanedData.loadBalancingEnabled === 'number') {
            cleanedData.loadBalancingEnabled = cleanedData.loadBalancingEnabled === 1;
        }

        if (cleanedData.description === null || cleanedData.description === '') {
            delete cleanedData.description;
        }
        if (cleanedData.location === null || cleanedData.location === '') {
            delete cleanedData.location;
        }
        if (cleanedData.lmsTemplateId === null || cleanedData.lmsTemplateId === '') {
            delete cleanedData.lmsTemplateId;
        }
        if (cleanedData.ocppServerId === null || cleanedData.ocppServerId === '') {
            delete cleanedData.ocppServerId;
        }

        delete cleanedData.siteName;
        delete cleanedData.chargerStats;
        delete cleanedData.createdAt;
        delete cleanedData.updatedAt;
        delete cleanedData.lastStatusUpdate;
        delete cleanedData.currentPower;
        delete cleanedData.siteId;

        return cleanedData;
    };

    return (
        <Edit
            title={false}
            component="div"
            redirect="list"
            transform={transform}
            mutationMode="pessimistic"
            queryOptions={{ refetchOnMount: 'always' }}
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.load-groups.notifications.updated', { type: 'success' });
                    redirect('/load-groups');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapLoadGroupBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(loadGroupFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    {/* Statistics Overview */}
                    <FormDataConsumer>
                        {({ formData }) => {
                            const stats = formData?.chargerStats;
                            if (!stats) return null;

                            return (
                                <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                            <Assessment sx={{ mr: 2, color: '#00C853' }} />
                                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                {translate('resources.load-groups.sections.statistics')}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'grid', gap: { xs: 2, sm: 3 }, gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(200px, 1fr))' } }}>
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <EvStation sx={{ fontSize: 20, color: '#00C853' }} />
                                                    <Typography variant="subtitle2">
                                                        {translate('resources.load-groups.stats.chargers')}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 600 }}>
                                                    {stats.activeChargers} / {stats.totalChargers}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('resources.load-groups.stats.activeChargers')}
                                                </Typography>
                                            </Box>

                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Bolt sx={{ fontSize: 20, color: '#FFA726' }} />
                                                    <Typography variant="subtitle2">
                                                        {translate('resources.load-groups.stats.powerUtilization')}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 600 }}>
                                                    {stats.powerUtilizationPercent}%
                                                </Typography>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={stats.powerUtilizationPercent}
                                                    sx={{
                                                        mt: 1,
                                                        height: 8,
                                                        borderRadius: 4,
                                                        backgroundColor: '#E0E0E0',
                                                        '& .MuiLinearProgress-bar': {
                                                            backgroundColor: stats.powerUtilizationPercent > 80 ? '#FF6B35' : '#00C853',
                                                            borderRadius: 4,
                                                        }
                                                    }}
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    {stats.totalCurrentPower?.toFixed(1)} / {formData.maxPower} kW
                                                </Typography>
                                            </Box>

                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Power sx={{ fontSize: 20, color: '#1976D2' }} />
                                                    <Typography variant="subtitle2">
                                                        {translate('resources.load-groups.stats.capacity')}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 600 }}>
                                                    {stats.utilizationPercent}%
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {stats.totalMaxPower?.toFixed(1)} / {formData.maxPower} kW {translate('resources.load-groups.stats.allocated')}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {stats.utilizationPercent > 90 && (
                                            <Alert severity="warning" sx={{ mt: 2 }}>
                                                {translate('resources.load-groups.warnings.highUtilization')}
                                            </Alert>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        }}
                    </FormDataConsumer>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 } }}>
                        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                            <Tab label={translate('resources.load-groups.tabs.details')} />
                            <Tab label={translate('resources.load-groups.tabs.chargers')} />
                        </Tabs>
                    </Box>

                    <TabPanel value={tabValue} index={0}>
                        {/* Basic Information and Site */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <LocationOn sx={{ mr: 2, color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.load-groups.sections.basic')}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                            {translate('resources.load-groups.fields.site')}
                                        </Typography>
                                        <FormDataConsumer>
                                            {({ formData }) => (
                                                <span style={{ pointerEvents: 'none' }}>
                                                    <Chip
                                                        label={formData?.siteName || formData?.siteId || ''}
                                                        component="div"
                                                        clickable={false}
                                                        sx={{
                                                            backgroundColor: '#00C85315',
                                                            color: '#00C853',
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                </span>
                                            )}
                                        </FormDataConsumer>
                                        <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                                            {translate('resources.load-groups.helpers.siteCannotChange')}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="name"
                                            validate={[
                                                required(),
                                                maxLength(100, translate("resources.load-groups.validation.nameTooLong")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.load-groups.helpers.name')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>

                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="location"
                                            validate={[
                                                maxLength(200, translate("resources.load-groups.validation.locationTooLong")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.load-groups.helpers.location')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>

                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="description"
                                            multiline
                                            rows={3}
                                            validate={[
                                                maxLength(500, translate("resources.load-groups.validation.descriptionTooLong")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.load-groups.helpers.description')}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Electrical Configuration */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <ElectricBolt sx={{ mr: 2, color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.load-groups.sections.electrical')}
                                    </Typography>
                                </Box>

                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        {translate('resources.load-groups.warnings.electricalChanges')}
                                    </Typography>
                                </Alert>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <SelectInput
                                                source="phase"
                                                choices={[
                                                    { id: 'single', name: translate('resources.load-groups.phases.single') },
                                                    { id: 'three', name: translate('resources.load-groups.phases.three') },
                                                ]}
                                                validate={[required()]}
                                                fullWidth
                                                helperText={translate('resources.load-groups.helpers.phase')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="voltage"
                                                validate={[
                                                    required(),
                                                    minValue(100, translate("resources.load-groups.validation.voltageTooLow")),
                                                    maxValue(1000, translate("resources.load-groups.validation.voltageTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.load-groups.helpers.voltage')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="maxCurrent"
                                                validate={[
                                                    required(),
                                                    minValue(0, translate("resources.load-groups.validation.currentNegative")),
                                                    maxValue(5000, translate("resources.load-groups.validation.currentTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.load-groups.helpers.maxCurrent')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="maxPower"
                                                validate={[
                                                    required(),
                                                    minValue(1, translate("resources.load-groups.validation.powerTooLow")),
                                                    maxValue(2000, translate("resources.load-groups.validation.powerTooHigh")),
                                                ]}
                                                defaultValue={100}
                                                fullWidth
                                                helperText={translate('resources.load-groups.helpers.maxPower')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                    </Box>

                                    <FormDataConsumer>
                                        {({ formData }) => {
                                            if (formData.phase === 'single' && formData.voltage > 480) {
                                                return (
                                                    <Alert severity="error">
                                                        {translate('resources.load-groups.validation.singlePhaseVoltage')}
                                                    </Alert>
                                                );
                                            }
                                            return null;
                                        }}
                                    </FormDataConsumer>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Power Management Settings */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <Power sx={{ mr: 2, color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.load-groups.sections.powerManagement')}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="priorityLevel"
                                                validate={[
                                                    required(),
                                                    minValue(1, translate("resources.load-groups.validation.priorityTooLow")),
                                                    maxValue(10, translate("resources.load-groups.validation.priorityTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.load-groups.helpers.priorityLevel')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <SelectInput
                                                source="status"
                                                choices={[
                                                    { id: 'active', name: translate('resources.load-groups.statuses.active') },
                                                    { id: 'inactive', name: translate('resources.load-groups.statuses.inactive') },
                                                    { id: 'maintenance', name: translate('resources.load-groups.statuses.maintenance') },
                                                ]}
                                                validate={[required()]}
                                                fullWidth
                                                helperText={translate('resources.load-groups.helpers.status')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                    </Box>

                                    <BooleanInput
                                        source="loadBalancingEnabled"
                                        sx={{ mb: 2 }}
                                    />

                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <ReferenceInput
                                                source="ocppServerId"
                                                reference="backend/ocpp-servers"
                                                filter={{ status: 'active' }}
                                                sort={{ field: 'name', order: 'ASC' }}
                                                perPage={1000}
                                            >
                                                <SelectInput
                                                    optionText="name"
                                                    fullWidth
                                                    emptyText="None"
                                                    helperText={translate('resources.load-groups.helpers.ocppServer')}
                                                    sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                                />
                                            </ReferenceInput>
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <ReferenceInput
                                                source="lmsTemplateId"
                                                reference="backend/lms-templates"
                                                filter={{ all: 'true' }}
                                                sort={{ field: 'name', order: 'ASC' }}
                                                perPage={1000}
                                                filterToQuery={(searchText: string) => ({ search: searchText, all: 'true' })}
                                            >
                                                <AutocompleteInput
                                                    optionText="name"
                                                    fullWidth
                                                    emptyText="None"
                                                    helperText={translate('resources.load-groups.helpers.lmsTemplate')}
                                                    sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                                    filterToQuery={(searchText: string) => searchText}
                                                    shouldRenderSuggestions={(val: string) => val.trim().length >= 0}
                                                />
                                            </ReferenceInput>
                                        </Box>
                                    </Box>

                                    <Alert severity="info">
                                        {translate('resources.load-groups.info.priorityNote')}
                                    </Alert>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Delete Load Group Section */}
                        <Card sx={{ overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <Delete sx={{ mr: 2, color: '#F44336' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.load-groups.delete.sectionTitle')}
                                    </Typography>
                                </Box>

                                <DeleteLoadGroupSection />
                            </CardContent>
                        </Card>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <Card sx={{ overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <ChargersListSection />
                            </CardContent>
                        </Card>
                    </TabPanel>
                </Box>
            </SimpleForm>
        </Edit>
    );
};